import CommonHeader from '@/src/components/CommonHeader';
import Configuration from '@/src/components/Configuration/Configuration';
import NoPermission from '@/src/components/NoPermission';
import SelectObjectAsValue from '@/src/components/SelectObjectAsValue';
import useBreadCrumbData from '@/src/pages/SalesCannelVersion/Hooks/useBreadCrumbData';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import {
  // InfoCircleOutlined,
  CaretUpOutlined,
} from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Form, Spin } from 'antd';
import React from 'react';
import { useHistory, useParams } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { msg } from '@/src/tools/message';
import {
  getLanguageSetting,
  getLanguageSettingTranslation,
  setLanguageSetting,
} from 'src/request/salesChannel/setting/language';
import { resetLanguageState } from 'src/constrains/SalesChannelMultiLanguageSelect/slice';
import { useDispatch } from 'react-redux';
import { i18nGet } from 'src/tools/tools';
import AvailableLanguagesTable from './Components/AvailableLanguagesTable';
import ShopperTransSettings from './Components/ShopperTransSettings';
import styles from './style.module.less';

const permissions = (uuid: string) => {
  const view_language_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelLanguage,
        operation: EnumOperation.view_language_setting,
        uuid,
      }),
    ],
  });
  const edit_language_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelLanguage,
        operation: EnumOperation.edit_language_setting,
        uuid,
      }),
    ],
  });

  return {
    view_language_setting,
    edit_language_setting,
  };
};

const LanguageSetting = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const history = useHistory();
  const dispatch = useDispatch();
  const { uuid } = useParams<{ uuid: string }>();
  const { loading: breadCrumbLoading, breadcrumbData } = useBreadCrumbData({
    lastSecond: getI18n('general.sider.settings'),
    lastOne: getI18n('salesChannelLanguage.index.languages'),
  });
  const { view_language_setting, edit_language_setting } = permissions(uuid);
  // const [searchList, setSearchList] = React.useState<string[]>([]);
  const [form] = Form.useForm();
  // 保存按钮是否置灰
  const [onSaveDisable, setOnSaveDisable] = React.useState<boolean>(true);

  const [offLine, setOffLine] = React.useState<boolean>(false);
  const [defaultTransSetting, setDefaultTransSetting] = React.useState(null);

  const onValuesChange = () => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };

  const onSubmit = () => {
    form.validateFields().then((res) => {
      // console.log('onSubmit: ', res);
      _setLanguageSetting(res);
    });
  };

  const onGoBackClick = () => {
    history.push(`/salesChannel/settings/${uuid}`);
  };

  const { loading: getLanguageSettingLoading, run: _getLanguageSetting } =
    useRequest(() => getLanguageSetting(uuid), {
      manual: true,
      ready: !!view_language_setting,
      onSuccess(data) {
        setOffLine(data.salesChannelOffline);
        setDefaultTransSetting(data.defaultTransSetting);
        form.setFieldsValue({
          availableLanguages: data.availableLanguages,
          primaryLanguage: data.primaryLanguage,
          // 所有用于展示的数据列表, 第一列跟第二列初始值一模一样，第一列不可以修改只做展示用，第二列可以修改
          shopperTransSettings: data.shopperTransSettings,
        });
      },
    });
  const { loading: setLanguageSettingLoading, run: _setLanguageSetting } =
    useRequest(
      (params) =>
        setLanguageSetting({
          ...params,
          salesChannelUuid: uuid,
        }),
      {
        manual: true,
        onSuccess(result, params: any) {
          const availableLanguages =
            result?.availableLanguages || params?.availableLanguages || [];

          const primaryLanguageField =
            result?.primaryLanguage || params?.primaryLanguage;
          const primaryLanguageTag =
            typeof primaryLanguageField === 'string'
              ? primaryLanguageField
              : primaryLanguageField?.tag || null;

          dispatch(
            resetLanguageState({
              salesChannelUuid: uuid,
              languageList: availableLanguages,
              primaryLanguage: primaryLanguageTag,
            }),
          );
          history.push(`/salesChannel/settings/${uuid}`);
          msg.success(getI18n('general.savesuccess'));
        },
      },
    );

  const onAvailableLanguagesChange = async (list, type) => {
    setOnSaveDisable(false);
    const oldShopperTransSettings = form.getFieldValue('shopperTransSettings');
    switch (type) {
      case 'ADD':
        const lastIndex = list.length - 1;
        const newLanguageTag = list[lastIndex].languageTag;
        const result = await getLanguageSettingTranslation(
          uuid,
          newLanguageTag,
        );
        form.setFieldsValue({
          availableLanguages: list,
          shopperTransSettings: oldShopperTransSettings.concat(
            result.locale === newLanguageTag
              ? result
              : {
                  ...result,
                  locale: newLanguageTag,
                },
          ),
        });

        break;
      case 'SORT':
        form.setFieldsValue({
          availableLanguages: list,
          shopperTransSettings: list.map((i) => {
            return oldShopperTransSettings.find((u) => u.locale === i.tag);
          }),
        });
        break;
      case 'DEL':
        const nl = list.reduce((a, i) => {
          const item = oldShopperTransSettings.find((u) => u.locale === i.tag);
          return a.concat(item);
        }, []);
        form.setFieldsValue({
          shopperTransSettings: nl,
        });

        break;
      case 'ADD_TRANSLATE':
        form.setFieldsValue({
          availableLanguages: list,
        });

        break;
    }
  };
  const onImportEnd = () => {
    // 重新请求
    _getLanguageSetting();
  };
  React.useEffect(() => {
    if (view_language_setting) {
      _getLanguageSetting();
    }
  }, []);

  return view_language_setting ? (
    <div className={styles.container}>
      <Spin
        spinning={
          breadCrumbLoading ||
          getLanguageSettingLoading ||
          setLanguageSettingLoading
        }>
        <Form
          name={'languageSetting'}
          form={form}
          onValuesChange={onValuesChange}
          className={styles.editForm}>
          <CommonHeader
            breadcrumbData={breadcrumbData}
            onSaveDisable={onSaveDisable || !edit_language_setting}
            onSubmit={onSubmit}
            onDiscard={onGoBackClick}
            onGoBackClick={onGoBackClick}
          />
          <div className={styles.languageContainer}>
            <div className={styles.availableLanguage}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n('salesChannelLanguage.index.languages')}
                </div>
              </div>
              <div className={styles.content}>
                <div className={styles.available}>
                  <div className={styles.itemTitle}>
                    {getI18n('salesChannelLanguage.index.availableLanguages')}
                  </div>
                  {/* 这里需要初始值为[] ,当从后台获取到数据之后,长度更改,触发 primaryLanguage 的选择器重渲染 */}
                  <Form.Item initialValue={[]} name={'availableLanguages'}>
                    <AvailableLanguagesTable
                      onCustomChange={onAvailableLanguagesChange}
                      disableEdit={!edit_language_setting}
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  noStyle
                  shouldUpdate={(p, c) => {
                    const shouldUpdate =
                      p.availableLanguages &&
                      c.availableLanguages &&
                      p.availableLanguages.length !==
                        c.availableLanguages.length;

                    return shouldUpdate;
                  }}>
                  {({ getFieldValue }) => {
                    const availableLanguages =
                      getFieldValue('availableLanguages') || [];

                    return (
                      <div className={styles.primaryLanguage}>
                        <div className={styles.itemTitle}>
                          {getI18n(
                            'salesChannelLanguage.index.primaryLanguage',
                          )}
                        </div>
                        <Form.Item name={['primaryLanguage']}>
                          <SelectObjectAsValue
                            showSearch
                            objectAsValue
                            options={availableLanguages}
                            disabled={!offLine || !edit_language_setting}
                            labelKey='description'
                            valueKey='tag'
                            size='large'
                            suffixIcon={
                              <CaretUpOutlined className={styles.selectIcon} />
                            }
                            style={{
                              width: '528px',
                            }}></SelectObjectAsValue>
                        </Form.Item>
                        <div className={styles.tipText}>
                          <Configuration
                            hideGt={true}
                            configStart={getI18n(
                              'salesChannelLanguage.index.tipTextStart',
                            )}
                            configLink={getI18n(
                              'salesChannelLanguage.index.tipTextLink',
                            )}
                            configEnd={getI18n(
                              'salesChannelLanguage.index.tipTextEnd',
                            )}
                          />
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>
              </div>
            </div>

            <div className={styles.translations}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n('salesChannelLanguage.index.translations')}
                </div>
              </div>
              <div className={styles.content}>
                {/* 
                      注: 在添加  availableLanguages 时,会请求接口 重新set Form set shopperTransSettings
                  */}
                <Form.Item name={['shopperTransSettings']}>
                  <ShopperTransSettings
                    defaultTransSetting={defaultTransSetting}
                    onImportEnd={onImportEnd}
                    disableEdit={!edit_language_setting}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  ) : (
    <NoPermission />
  );
};
export default LanguageSetting;
